import SearchableDropdown from '../../../common/SearchableDropdown';
import { BaseControl } from '.';
import { db } from '../../../../firebase/config';
import { client } from '../../../../hooks/Client';
import notDeleted from '../../../../helpers/notDeleted';
import {
    PERSONA_PROFILE_FIXED_FIELDS,
    PERSONA_DATA_FIELD_PREFIX,
    isForbiddenPersonaDataTitle,
} from '../../../../helpers/liveReportHelpers/personaWidgetTypes';

// AI-CONTEXT: See docs/features/livereport-per-deelnemer.md (T9)
// Meerkeuze met volgorde voor 'Traject en gegevens'. Nodig omdat geen
// bestaande control een geordende lijst sleutels opslaat. Opties: de vaste
// velden plus elke gegevenstitel (`PersonaData.Title`) van de organisatie als
// `data:<Title>` — exact de sleutels die de server leest
// (functions/liveReports/publicPersonaData.js).
//
// De titels komen uit één lezing van alle PersonaData van de organisatie, één
// keer per control (BaseControl.load). Er is geen aparte lijst met
// gegevensdefinities: titels zijn vrije tekst en komen ook uit intakeformulieren.
//
// Identificatienummers (BSN, IBAN, …) zijn nooit te kiezen
// (isForbiddenPersonaDataTitle). Een oude keuze verdwijnt uit de lijst en
// valt bij de volgende wijziging uit Settings.fields.

/**
 * @typedef {object} PersonaFieldChoice
 * @property {string} value - Sleutel voor Settings.fields
 * @property {string} label - Naam van het veld
 */

/**
 * @param {string} key
 * @returns {string}
 */
const labelForKey = (key) => {
    if (PERSONA_PROFILE_FIXED_FIELDS[key]) return PERSONA_PROFILE_FIXED_FIELDS[key].label;
    if (key.startsWith(PERSONA_DATA_FIELD_PREFIX)) return key.slice(PERSONA_DATA_FIELD_PREFIX.length);
    return key;
};

/**
 * @param {string} key
 * @returns {boolean} false voor een identificatienummer
 */
const isAllowedKey = (key) => !(key.startsWith(PERSONA_DATA_FIELD_PREFIX)
    && isForbiddenPersonaDataTitle(key.slice(PERSONA_DATA_FIELD_PREFIX.length)));

/** @returns {Promise<{choices: Array<PersonaFieldChoice>}>} */
const loadChoices = async () => {
    // Direct, dus tenant en Deleted met de hand
    // (docs/pitfalls.md#database-missing-compagnyid-on-fresh-fetch).
    const snap = await db.collection('PersonaData').where('CompagnyID', '==', client || '').get();
    const titles = [...new Set(snap.docs
        .map((d) => d.data())
        .filter(notDeleted)
        .map((d) => d.Title)
        .filter((title) => typeof title === 'string' && title.trim() !== '')
        .filter((title) => !isForbiddenPersonaDataTitle(title)))]
        .sort((a, b) => a.localeCompare(b, 'nl'));

    const fixed = Object.keys(PERSONA_PROFILE_FIXED_FIELDS)
        .map((key) => ({ value: key, label: PERSONA_PROFILE_FIXED_FIELDS[key].label }));
    const data = titles.map((title) => ({ value: `${PERSONA_DATA_FIELD_PREFIX}${title}`, label: title }));
    return { choices: [...fixed, ...data] };
};

/**
 * @param {Array<string>} list
 * @param {number} from
 * @param {number} to
 * @returns {Array<string>}
 */
const move = (list, from, to) => {
    const next = [...list];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    return next;
};

/**
 * @extends {BaseControl<Array<string>>}
 */
export class PersonaFieldsControl extends BaseControl {

    /**
     * @param {{ label: string, description?: string }} args
     */
    constructor({ label, description }) {
        super({ label, description, defaultValue: [], options: loadChoices });
    }

    /** @type {BaseControl<Array<string>>['render']} */
    render(value, onChange) {
        const chosen = (Array.isArray(value) ? value : []).filter((key) => typeof key === 'string' && isAllowedKey(key));
        /** @type {Array<PersonaFieldChoice>} */
        const choices = this.options?.choices || [];
        const available = choices.filter((c) => !chosen.includes(c.value));

        return (
            <div className="select-control persona-fields-control">
                <span className="select-control-label">{this.label}</span>
                {chosen.length === 0
                    ? <p className="persona-fields-control-empty">Nog geen velden gekozen.</p>
                    : (
                        <ol className="persona-fields-control-list">
                            {chosen.map((key, index) => {
                                const label = labelForKey(key);
                                return (
                                    <li key={key} className="persona-fields-control-item">
                                        <span className="persona-fields-control-name">{label}</span>
                                        <button
                                            type="button"
                                            aria-label={`${label} omhoog`}
                                            disabled={index === 0}
                                            onClick={() => onChange(move(chosen, index, index - 1))}
                                        >↑</button>
                                        <button
                                            type="button"
                                            aria-label={`${label} omlaag`}
                                            disabled={index === chosen.length - 1}
                                            onClick={() => onChange(move(chosen, index, index + 1))}
                                        >↓</button>
                                        <button
                                            type="button"
                                            aria-label={`${label} verwijderen`}
                                            onClick={() => onChange(chosen.filter((k) => k !== key))}
                                        >×</button>
                                    </li>
                                );
                            })}
                        </ol>
                    )}
                <SearchableDropdown
                    options={available}
                    value=""
                    placeholder="Veld toevoegen…"
                    onChange={(key) => { if (key) onChange([...chosen, key]); }}
                    allowSearch
                />
                {this.description && (
                    <p className="select-control-description">{this.description}</p>
                )}
            </div>
        );
    }
}
